// Feature 09: Distribution Signal Intelligence Page

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Refresh,
  Edit,
  CalendarToday,
  TrendingUp,
  Info,
} from '@mui/icons-material';
import {
  getPageDistributionSignals,
  annotateSpikeSource,
  recomputeDistributionSignal,
} from '../services/distributionSignalService';
import DistributionSignalBadge from '../components/distributionSignal/DistributionSignalBadge';
import SpikePlatformChart from '../components/distributionSignal/SpikePlatformChart';
import { DistributionSignal, SpikeAnnotation } from '../types/distributionSignal.types';

const DistributionSignalPage: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const queryClient = useQueryClient();

  const [openAnnotation, setOpenAnnotation] = useState(false);
  const [selectedSpike, setSelectedSpike] = useState<string | null>(null);
  const [annotationText, setAnnotationText] = useState('');
  const [annotationSource, setAnnotationSource] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Fetch distribution signals for the page
  const { data: signals, isLoading, error } = useQuery({
    queryKey: ['distributionSignals', pageId],
    queryFn: () => getPageDistributionSignals(pageId!, thirtyDaysAgo, today),
    enabled: !!pageId,
    refetchInterval: 300000, // 5 minutes
  });

  // Annotate spike mutation
  const annotateMutation = useMutation({
    mutationFn: (spikeId: string) => {
      const annotation: SpikeAnnotation = {
        spikeId,
        annotationText,
        source: annotationSource,
        verified: true,
      };
      return annotateSpikeSource(spikeId, annotation);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distributionSignals', pageId] });
      setOpenAnnotation(false);
      setAnnotationText('');
      setAnnotationSource('');
      setSelectedSpike(null);
    },
  });

  // Recompute mutation
  const recomputeMutation = useMutation({
    mutationFn: (spikeId: string) => recomputeDistributionSignal(spikeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distributionSignals', pageId] });
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load distribution signals. Please try again later.
      </Alert>
    );
  }

  const hasSignals = signals && signals.length > 0;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Distribution Signal Intelligence</Typography>
      </Box>

      {/* Information Banner */}
      <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Distribution Signals:</strong> Detects traffic spikes and correlates them with GA4
          platform data to identify the source. This helps understand where traffic increases come
          from (social media, referral links, etc.).
        </Typography>
      </Alert>

      {/* Spike Results */}
      {!hasSignals ? (
        <Alert severity="info">No traffic spikes detected in the last 30 days.</Alert>
      ) : (
        <Grid container spacing={3}>
          {signals.map((signal: DistributionSignal) => (
            <Grid item xs={12} key={signal.spike.id}>
              <Card variant="outlined">
                <CardContent>
                  {/* Spike Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <TrendingUp color="primary" />
                        <Typography variant="h6">
                          Traffic Spike: {signal.spike.spikeMagnitude.toFixed(1)}x
                        </Typography>
                        <DistributionSignalBadge
                          spikeMagnitude={signal.spike.spikeMagnitude}
                          durationDays={signal.spike.durationDays}
                          isAnnotated={signal.spike.isAnnotated}
                        />
                      </Box>
                      <Box display="flex" gap={1} alignItems="center" color="text.secondary">
                        <CalendarToday fontSize="small" />
                        <Typography variant="body2">
                          {new Date(signal.spike.startDate).toLocaleDateString()} to{' '}
                          {new Date(signal.spike.endDate).toLocaleDateString()}
                        </Typography>
                        <Chip
                          label={`${signal.spike.baselineClicks.toLocaleString()} → ${signal.spike.peakClicks.toLocaleString()} clicks`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>

                    <Stack direction="row" gap={1}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => {
                          setSelectedSpike(signal.spike.id);
                          setAnnotationText(signal.spike.annotationText || '');
                          setOpenAnnotation(true);
                        }}
                      >
                        Annotate
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Refresh />}
                        onClick={() => recomputeMutation.mutate(signal.spike.id)}
                        disabled={recomputeMutation.isPending}
                      >
                        Recompute
                      </Button>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* GA4 Breakdown */}
                  {signal.ga4Available && signal.ga4Breakdown && signal.ga4Breakdown.length > 0 ? (
                    <SpikePlatformChart
                      breakdown={signal.ga4Breakdown}
                      totalSessions={signal.ga4Breakdown.reduce((sum: number, p) => sum + p.sessions, 0)}
                      spike={signal.spike}
                    />
                  ) : (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      GA4 data not available. Spike detected but platform breakdown unavailable.
                    </Alert>
                  )}

                  {/* Annotation */}
                  {signal.spike.isAnnotated && signal.spike.annotationText && (
                    <Box mt={2} p={1.5} bgcolor="success.lighter" borderRadius={1}>
                      <Typography variant="subtitle2" gutterBottom>
                        Annotation:
                      </Typography>
                      <Typography variant="body2">{signal.spike.annotationText}</Typography>
                      {signal.spike.annotatedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Annotated: {new Date(signal.spike.annotatedAt).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Annotation Dialog */}
      <Dialog open={openAnnotation} onClose={() => setOpenAnnotation(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Annotate Spike Source</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Identify where this traffic spike came from
          </Typography>
          <TextField
            label="Traffic Source"
            placeholder="e.g., Reddit /r/javascript, Twitter viral thread, Product Hunt"
            value={annotationSource}
            onChange={(e) => setAnnotationSource(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="Notes"
            placeholder="Additional context about this spike"
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAnnotation(false)}>Cancel</Button>
          <Button
            onClick={() => selectedSpike && annotateMutation.mutate(selectedSpike)}
            variant="contained"
            disabled={annotateMutation.isPending || !annotationText}
          >
            Save Annotation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DistributionSignalPage;
